import React, { useMemo, useRef } from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import PropTypes from 'prop-types';
import Icon from '@/components/Icon';
import CardActions from './CardActions';

const ACCENT = '#4c9b5f';

const capitalize = value => value.charAt(0).toUpperCase() + value.slice(1);

const formatExerciseSpec = exercise => {
  if (exercise.sets && exercise.reps) return `${exercise.sets} × ${exercise.reps}`;
  if (exercise.reps) return exercise.reps;
  if (exercise.sets) return `${exercise.sets} sets`;
  return '';
};

function MetaStat({ icon, label, value, colors }) {
  return (
    <View style={styles.metaStat}>
      <Icon name={icon} size={14} color={colors.accent} />
      <Text style={[styles.metaLabel, { color: colors.secondary }]}>{label}</Text>
      <Text style={[styles.metaValue, { color: colors.primary }]} numberOfLines={1}>{value}</Text>
    </View>
  );
}

MetaStat.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  colors: PropTypes.object.isRequired,
};

function WorkoutCard({ spec, colors: themeColors, conversationId, messageId }) {
  const captureTargetRef = useRef(null);

  const colors = useMemo(() => ({
    primary: themeColors.text_color_primary || '#e6e6e6',
    secondary: themeColors.text_color_secondary || '#a6a6a6',
    border: themeColors.border_color_input || '#2a2a2a',
    background: themeColors.solid_color_secondary || '#1c1c1c',
    band: themeColors.solid_color_primary || '#141414',
    accent: ACCENT,
  }), [themeColors]);

  const meta = useMemo(() => [
    { icon: 'clock', label: 'Duration', value: spec.duration },
    { icon: 'bolt', label: 'Level', value: spec.difficulty ? capitalize(spec.difficulty) : '' },
    { icon: 'arrows-rotate', label: 'Rounds', value: spec.rounds },
    { icon: 'list-ol', label: 'Exercises', value: spec.exerciseCount > 0 ? String(spec.exerciseCount) : '' },
    { icon: 'fire', label: 'Burn', value: spec.calories },
  ].filter(item => item.value), [spec]);

  const numberedGroups = useMemo(() => {
    let number = 0;
    return spec.exerciseGroups.map(group => ({
      ...group,
      items: group.items.map(item => ({ ...item, number: (number += 1) })),
    }));
  }, [spec]);

  return (
    <View
      accessibilityRole="summary"
      accessibilityLabel={`Workout: ${spec.title}. ${spec.exerciseCount} exercises.`}
      style={[styles.container, { backgroundColor: colors.background, borderColor: colors.border }]}
    >
      <View
        ref={captureTargetRef}
        collapsable={false}
        dataSet={{ cardCapture: '' }}
        style={[styles.captureTarget, { backgroundColor: colors.background }]}
      >
        <View style={styles.header}>
          <View style={[styles.headerIcon, { backgroundColor: `${ACCENT}26`, borderColor: `${ACCENT}55` }]}>
            <Icon name="dumbbell" size={17} color={ACCENT} />
          </View>
          <View style={styles.headerText}>
            <Text style={[styles.title, { color: colors.primary }]}>{spec.title}</Text>
            {!!spec.description && (
              <Text style={[styles.description, { color: colors.secondary }]}>{spec.description}</Text>
            )}
          </View>
        </View>

        {spec.equipment.length > 0 && (
          <View style={styles.tags}>
            {spec.equipment.map(item => (
              <View key={item} style={[styles.tag, { borderColor: colors.border, backgroundColor: colors.band }]}>
                <Text style={[styles.tagText, { color: colors.secondary }]}>{item}</Text>
              </View>
            ))}
          </View>
        )}

        {meta.length > 0 && (
          <View style={[styles.metaBand, { backgroundColor: colors.band, borderColor: colors.border }]}>
            {meta.map(item => (
              <MetaStat key={item.label} icon={item.icon} label={item.label} value={item.value} colors={colors} />
            ))}
          </View>
        )}

        <View style={styles.sectionHeader}>
          <Icon name="heart-pulse" size={14} color={ACCENT} style={styles.sectionHeaderIcon} />
          <Text style={[styles.sectionTitle, { color: colors.primary }]}>Exercises</Text>
        </View>
        {numberedGroups.map((group, groupIndex) => (
          <View key={`group-${groupIndex}`} style={styles.exerciseGroup}>
            {!!group.section && (
              <Text style={[styles.exerciseSection, { color: colors.secondary }]}>{group.section}</Text>
            )}
            <View>
              {group.items.map((exercise, index) => {
                const exerciseSpec = formatExerciseSpec(exercise);
                return (
                  <View key={`exercise-${groupIndex}-${index}`} style={styles.exerciseItem}>
                    <View style={[styles.exerciseNumber, { backgroundColor: `${ACCENT}26`, borderColor: `${ACCENT}55` }]}>
                      <Text style={[styles.exerciseNumberText, { color: ACCENT }]}>{exercise.number}</Text>
                    </View>
                    <View style={styles.exerciseBody}>
                      <Text style={[styles.exerciseName, { color: colors.primary }]}>{exercise.name}</Text>
                      {!!exercise.notes && (
                        <Text style={[styles.exerciseNotes, { color: colors.secondary }]}>{exercise.notes}</Text>
                      )}
                    </View>
                    <View style={styles.exerciseSpec}>
                      {!!exerciseSpec && (
                        <Text style={[styles.exerciseSpecText, { color: colors.primary }]}>{exerciseSpec}</Text>
                      )}
                      {!!exercise.rest && (
                        <Text style={[styles.exerciseRest, { color: colors.secondary }]}>Rest {exercise.rest}</Text>
                      )}
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        ))}

        {spec.tips.length > 0 && (
          <View style={[styles.tipsBox, { borderLeftColor: ACCENT, backgroundColor: colors.band }]}>
            <View style={styles.tipsHeader}>
              <Icon name="lightbulb" size={13} color={ACCENT} style={styles.tipsHeaderIcon} />
              <Text style={[styles.tipsTitle, { color: ACCENT }]}>Trainer Tips</Text>
            </View>
            {spec.tips.map((tip, index) => (
              <Text key={`tip-${index}`} style={[styles.tipText, { color: colors.secondary }]}>{tip}</Text>
            ))}
          </View>
        )}
      </View>

      <CardActions
        conversationId={conversationId}
        messageId={messageId}
        captureTargetRef={captureTargetRef}
        exportTitle={spec.title}
        accentColor={ACCENT}
        borderColor={colors.border}
        bandColor={colors.band}
        textColor={colors.secondary}
      />
    </View>
  );
}

WorkoutCard.propTypes = {
  spec: PropTypes.object.isRequired,
  colors: PropTypes.object.isRequired,
  conversationId: PropTypes.string,
  messageId: PropTypes.string,
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    alignSelf: 'stretch',
    flexShrink: 1,
    borderWidth: 1,
    borderRadius: 14,
    marginVertical: 8,
    position: 'relative',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 4px 18px rgba(0,0,0,0.22)',
    }),
  },
  captureTarget: {
    borderRadius: 14,
    padding: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  headerIcon: {
    width: 38,
    height: 38,
    borderRadius: 11,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
    marginRight: 12,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 19,
    fontWeight: '700',
    lineHeight: 25,
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
    marginTop: 4,
  },
  tags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 12,
  },
  tag: {
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 4,
    marginRight: 6,
    marginBottom: 6,
  },
  tagText: {
    fontSize: 12,
    fontWeight: '500',
  },
  metaBand: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderWidth: 1,
    borderRadius: 11,
    paddingVertical: 10,
    paddingHorizontal: 6,
    marginTop: 6,
  },
  metaStat: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 72,
    minWidth: 0,
    alignItems: 'center',
    paddingVertical: 2,
  },
  metaLabel: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginTop: 3,
  },
  metaValue: {
    fontSize: 14,
    fontWeight: '700',
    marginTop: 3,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 18,
    marginBottom: 9,
  },
  sectionHeaderIcon: {
    marginRight: 7,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.9,
  },
  exerciseGroup: {
    marginBottom: 4,
  },
  exerciseSection: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.4,
    marginTop: 6,
    marginBottom: 7,
  },
  exerciseItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  exerciseNumber: {
    width: 26,
    height: 26,
    borderRadius: 13,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
    marginTop: 1,
  },
  exerciseNumberText: {
    fontSize: 13,
    fontWeight: '700',
  },
  exerciseBody: {
    flex: 1,
    minWidth: 0,
  },
  exerciseName: {
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 20,
  },
  exerciseNotes: {
    fontSize: 13,
    lineHeight: 18,
    marginTop: 2,
  },
  exerciseSpec: {
    alignItems: 'flex-end',
    marginLeft: 12,
  },
  exerciseSpecText: {
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
  },
  exerciseRest: {
    fontSize: 12,
    lineHeight: 17,
    marginTop: 2,
  },
  tipsBox: {
    marginTop: 10,
    borderLeftWidth: 3,
    borderRadius: 8,
    paddingHorizontal: 13,
    paddingVertical: 11,
  },
  tipsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  tipsHeaderIcon: {
    marginRight: 7,
  },
  tipsTitle: {
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  tipText: {
    fontSize: 13,
    lineHeight: 19,
    marginTop: 4,
  },
});

export default React.memo(WorkoutCard);
